// ============================================================================================
// Profile — API CONTRACT (WP9a → WP9b screens: menu footer, Locker, Bean Card, rewards, HUD)
// ============================================================================================
// Data (§27.3, persisted under localStorage `shoveoff.profile.v1`):
//   Profile = { profileId, name, cosmetics, level, xp, bucks, owned, stats, challenges,
//               firstPromptsDone, lastPostcard, + WP9a additive: totalXp, sessions, promptUses }
//   level  Bean Level 1–40.   xp  XP inside the current level (0 ≤ xp < xpForLevel(level)).
//   owned  CATALOG indices bought in the shop (level and starter unlocks are derived, not stored).
//
// Pure helpers (import freely, no side effects):
//   ./progression  xpForLevel, levelInfo(p) → { level, xp, need, frac, capped }, addXp,
//                  isUnlocked(p, cat, id), itemStatus(p, cat, id, date) → { unlocked, state:
//                  'starter'|'level'|'owned'|'locked'|'shop', unlockLevel, price, inShop, equipped },
//                  shopForDate(date) → the 6 CatalogItems of that UTC day (THE rotation: buying
//                  checks it, so screens must list this one), buyItem, equipItem, unlocksBetween,
//                  matchXpLines, matchBucks, MatchRewards / RewardLine types.
//   ./challenges   CHALLENGES[18] { id, text, goal, unit }, dailyChallengeIds(date), utcDate().
//   ./prompts      createFirstPrompts({ bus, profiles, glyph }) → update(view) → PromptView | null:
//                  the §9.1 first-time context prompts (text + device glyphs, fade after 3 uses).
//   ./settings     createSettingsStore(bus) → get/set(path, v)/patch/replace/reset: §14.4 settings
//                  sanitized, persisted (`shoveoff.settings.v1`) and emitted on 'settings' (live
//                  apply); SETTINGS_SCHEMA describes every row for a generic Settings screen;
//                  pushGuideOn(settings, level).
//
// The live service (ONE per page; owns persistence and the 'profile' bus event):
//   const profiles = createProfileService({ bus })          // ./ProfileService
//   profiles.get(): Profile                  current snapshot; replaced (never mutated) on change
//   profiles.set(p)                          sanitize + persist + emit 'profile' (Locker saves etc.)
//   profiles.setName(name) / equip(cat, id) / setIdentityPref(i) → boolean (false when locked)
//   profiles.buy(cat, id) → 'ok'|'owned'|'notInShop'|'tooPoor'|'notForSale'
//   profiles.level() → LevelInfo · profiles.itemStatus(cat, id) · profiles.shop() → ShopSlot[]
//   profiles.challenges() → ChallengeView[3] { id, text, unit, progress, goal, done, xp, bucks }
//   profiles.lastRewards() → MatchRewards | null; profiles.onRewards(fn) → unsubscribe
//       (fires after 'match:end' once XP, level-ups, unlocks and Bucks are applied)
//   profiles.setPostcard(dataUrl) · profiles.resetProgress() · profiles.flush()
// It listens to the bus by itself: 'joined' (local slot), 'round:init' / 'round:end' /
// 'match:end' (online rounds only; LocalSession never emits these, so Practice never pays),
// sim events for live challenge progress (replays never count), 'ui:screen' (menu = left the
// room). It emits 'profile' on every change, 'challenge:progress' on progress, and a 'toast'
// { kind: 'challenge' } when a challenge completes ({ kind: 'levelup' } at match end).
//
// main.ts wiring: create it right after the bus (before the first 'joined'), then
//   deps.profile = () => profiles.get(); actions.saveProfile(p) → profiles.set(p);
//   actions.setName(n) → profiles.setName(n); LocalSession({ profile: () => profiles.get() }).
// ============================================================================================
import { TUNE } from '@shared/constants';
import { CATALOG, DEFAULT_COSMETICS, validateCosmetics, type Cosmetics } from '@shared/cosmetics';
import { MapId } from '@shared/ids';
import { cleanName, randomBeanName } from '@shared/names';
import { readJson, STORAGE_KEYS, writeJson } from './storage';

/** Bean Card totals. */
export interface LifetimeStats {
  matches: number; wins: number; rounds: number; kos: number; clutches: number;
  longestFlight: number;            // meters
  oldestPeelTicks: number;
  mapRounds: number[];              // rounds played per MapId (favorite map)
}

/** Today's 3 challenges (UTC date `YYYY-MM-DD`), as indices into the 18-challenge pool. */
export interface DailyChallenges { date: string; ids: number[]; progress: number[]; done: boolean[] }

export interface Profile {
  profileId: string;
  name: string;
  cosmetics: Cosmetics;
  level: number;
  /** XP earned inside the current level. */
  xp: number;
  bucks: number;
  owned: number[];                  // CATALOG indices (`catalogIndex`) of bought/unlocked items
  stats: LifetimeStats;
  challenges: DailyChallenges;
  firstPromptsDone: string[];
  lastPostcard: string | null;      // PNG data URL
  /** Lifetime XP (additive, WP9a). */
  totalXp: number;
  /** Page loads so far (first-time prompts show during the first 3, §9.1). Additive, WP9a. */
  sessions: number;
  /** Successful uses per first-time prompt id (each fades after 3). Additive, WP9a. */
  promptUses: Record<string, number>;
}

// ---- Defaults, validation and persistence -----------------------------------------------------

const MAP_COUNT = Object.keys(MapId).length;

function emptyStats(): LifetimeStats {
  return { matches: 0, wins: 0, rounds: 0, kos: 0, clutches: 0, longestFlight: 0, oldestPeelTicks: 0, mapRounds: new Array<number>(MAP_COUNT).fill(0) };
}

/** 128 random bits as hex; `crypto.getRandomValues` also works outside secure contexts. */
function randomId(): string {
  const words = crypto.getRandomValues(new Uint32Array(4));
  return Array.from(words, (w) => w.toString(16).padStart(8, '0')).join('');
}

/** A random bean name for the name prompt's default and dice button (§9.1). */
export function suggestBeanName(): string {
  return randomBeanName({ rng: crypto.getRandomValues(new Uint32Array(1))[0] });
}

/** A fresh level-1 profile. An empty `name` means the name prompt has not been answered yet. */
export function createProfile(): Profile {
  return {
    profileId: randomId(),
    name: '',
    cosmetics: { ...DEFAULT_COSMETICS },
    level: 1, xp: 0, bucks: 0,
    owned: [],
    stats: emptyStats(),
    challenges: { date: '', ids: [], progress: [], done: [] },
    firstPromptsDone: [],
    lastPostcard: null,
    totalXp: 0,
    sessions: 0,
    promptUses: {},
  };
}

type Obj = Record<string, unknown>;

function obj(v: unknown): Obj {
  return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Obj) : {};
}

function count(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : fallback;
}

function intArray(v: unknown, valid: (n: number) => boolean): number[] {
  return Array.isArray(v) ? v.filter((n): n is number => typeof n === 'number' && Number.isInteger(n) && valid(n)) : [];
}

/** A full, valid Profile from untrusted JSON; anything missing or invalid falls back to a fresh profile's value. */
export function sanitizeProfile(raw: unknown): Profile {
  const fresh = createProfile();
  const r = obj(raw);
  const stats = obj(r.stats);
  const ch = obj(r.challenges);
  const mapRounds = Array.isArray(stats.mapRounds) ? stats.mapRounds : [];
  const level = Math.min(TUNE.progression.maxLevel, Math.max(1, Math.floor(count(r.level, 1))));
  const need = TUNE.progression.levelBaseXp + TUNE.progression.levelStepXp * (level - 1);
  // Challenge slots stay aligned: an invalid id drops its progress and done flag with it.
  const rawIds = Array.isArray(ch.ids) ? ch.ids : [];
  const slots: { id: number; progress: number; done: boolean }[] = [];
  rawIds.forEach((id, i) => {
    if (typeof id !== 'number' || !Number.isInteger(id) || id < 0 || id >= TUNE.progression.challenges.poolSize || slots.some((s) => s.id === id)) return;
    slots.push({
      id,
      progress: Math.floor(count(Array.isArray(ch.progress) ? ch.progress[i] : 0)),
      done: Array.isArray(ch.done) && ch.done[i] === true,
    });
  });
  slots.length = Math.min(slots.length, TUNE.progression.challenges.perDay);
  const uses = obj(r.promptUses);
  const promptUses: Record<string, number> = {};
  for (const [k, v] of Object.entries(uses)) if (/^[a-z]{1,16}$/.test(k)) promptUses[k] = Math.floor(count(v));
  return {
    profileId: typeof r.profileId === 'string' && /^[0-9a-f]{8,64}$/.test(r.profileId) ? r.profileId : fresh.profileId,
    name: cleanName(r.name) ?? '',
    cosmetics: validateCosmetics(r.cosmetics),
    level,
    xp: Math.min(need - 1, Math.floor(count(r.xp))),
    bucks: Math.floor(count(r.bucks)),
    owned: [...new Set(intArray(r.owned, (i) => i >= 0 && i < CATALOG.length))],
    stats: {
      matches: count(stats.matches), wins: count(stats.wins), rounds: count(stats.rounds), kos: count(stats.kos),
      clutches: count(stats.clutches), longestFlight: count(stats.longestFlight), oldestPeelTicks: count(stats.oldestPeelTicks),
      mapRounds: fresh.stats.mapRounds.map((_, i) => count(mapRounds[i])),
    },
    challenges: {
      date: typeof ch.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(ch.date) ? ch.date : '',
      ids: slots.map((s) => s.id),
      progress: slots.map((s) => s.progress),
      done: slots.map((s) => s.done),
    },
    firstPromptsDone: Array.isArray(r.firstPromptsDone) ? [...new Set(r.firstPromptsDone.filter((s): s is string => typeof s === 'string' && s.length <= 16))] : [],
    lastPostcard: typeof r.lastPostcard === 'string' && r.lastPostcard.startsWith('data:image/png') ? r.lastPostcard : null,
    totalXp: Math.floor(count(r.totalXp)),
    sessions: Math.floor(count(r.sessions)),
    promptUses,
  };
}

/** The saved profile, or a fresh one when storage is empty, unavailable or corrupt. */
export function loadProfile(): Profile {
  return sanitizeProfile(readJson(STORAGE_KEYS.profile));
}

export function saveProfile(p: Profile): boolean {
  return writeJson(STORAGE_KEYS.profile, p);
}

/** Fresh progression, keeping identity: profileId, name, cosmetics (fixed up by the service) and sessions. */
export function resetProgression(p: Profile): Profile {
  const fresh = createProfile();
  return { ...fresh, profileId: p.profileId, name: p.name, cosmetics: { ...p.cosmetics }, sessions: p.sessions, firstPromptsDone: [...p.firstPromptsDone], promptUses: { ...p.promptUses } };
}

export { DEFAULT_COSMETICS };
